import { useState, useEffect } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, Copy, Trash2, FileText, Database, CheckCircle, AlertCircle } from "lucide-react";

const formatSize = (bytes) => {
  if (!bytes) return "?";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileIcon = (contentType) => {
  if (!contentType) return <FileText size={20} className="text-slate-400" />;
  if (contentType.startsWith("image/")) return <ImageIcon size={20} className="text-blue-500" />;
  return <FileText size={20} className="text-slate-400" />;
};

export default function MediaManager() {
  const [files, setFiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [storageStatus, setStorageStatus] = useState(null);

  const fetchFiles = async () => {
    try {
      const res = await api.get("/media");
      setFiles(res.data.files);
      setTotal(res.data.total);
    } catch { toast.error("Dosyalar yüklenemedi"); }
    finally { setLoading(false); }
  };

  const fetchStorageStatus = async () => {
    try {
      const res = await api.get("/media/storage-status");
      setStorageStatus(res.data);
    } catch { /* silent */ }
  };

  useEffect(() => { fetchFiles(); fetchStorageStatus(); }, []);

  const handleUpload = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    let successCount = 0;
    for (const file of Array.from(fileList)) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        successCount++;
      } catch (e) {
        toast.error(`${file.name} yüklenemedi`);
      }
    }
    if (successCount > 0) {
      toast.success(`${successCount} dosya yüklendi`);
      fetchFiles();
    }
    setUploading(false);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}" dosyasını silmek istiyor musunuz?`)) return;
    try {
      await api.delete(`/media/${id}`);
      toast.success("Dosya silindi");
      fetchFiles();
    } catch { toast.error("Silme başarısız"); }
  };

  const copyPath = (path) => {
    const { BACKEND_URL_BASE } = require("../services/api");
    navigator.clipboard.writeText(`${BACKEND_URL_BASE}/api/media/file/${path}`);
    toast.success("URL kopyalandı");
  };

  return (
    <div className="p-8 fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Medya Yöneticisi
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">{total} dosya depolandı</p>
        </div>
        {/* Storage backend badge */}
        {storageStatus && (
          <div
            data-testid="storage-status-badge"
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium ${
              storageStatus.backend === "r2"
                ? "bg-green-50 border-green-200 text-green-700"
                : "bg-amber-50 border-amber-200 text-amber-700"
            }`}
          >
            {storageStatus.backend === "r2" ? (
              <CheckCircle size={13} />
            ) : (
              <AlertCircle size={13} />
            )}
            <Database size={13} />
            {storageStatus.backend === "r2"
              ? `Cloudflare R2 — ${storageStatus.r2_bucket}`
              : "Emergent Storage (fallback)"}
          </div>
        )}
      </div>

      {/* Upload zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files); }}
        data-testid="upload-zone"
        className={`border-2 border-dashed rounded-lg p-8 text-center mb-6 transition-colors ${
          dragOver ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-slate-300"
        }`}
      >
        <Upload size={24} className="text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500 mb-3">
          Dosyaları sürükleyip bırakın veya seçin
        </p>
        <label>
          <Button
            variant="outline"
            size="sm"
            disabled={uploading}
            data-testid="upload-file-btn"
            className="h-8 text-xs cursor-pointer border-slate-200"
            asChild
          >
            <span>{uploading ? "Yükleniyor..." : "Dosya Seç"}</span>
          </Button>
          <input
            type="file"
            multiple
            accept="image/*,.pdf,.txt"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files)}
          />
        </label>
      </div>

      {/* File list */}
      {loading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-14 bg-slate-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : files.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
          <ImageIcon size={32} className="text-slate-200 mx-auto mb-2" />
          <p className="text-sm text-slate-400">Henüz dosya yüklenmemiş</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="media-files-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider w-10"></th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Dosya Adı</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider w-24">Boyut</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider w-40">Tarih</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider w-24">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {files.map((f, i) => (
                <tr key={f.id} className={`hover:bg-slate-50 ${i < files.length - 1 ? "border-b border-slate-100" : ""}`}
                  data-testid={`media-row-${f.id}`}>
                  <td className="px-4 py-3">{getFileIcon(f.content_type)}</td>
                  <td className="px-4 py-3">
                    <p className="text-slate-800 font-medium truncate max-w-xs">{f.original_filename}</p>
                    <p className="text-xs text-slate-400 font-mono truncate max-w-xs">{f.storage_path}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">{formatSize(f.size)}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {f.created_at ? new Date(f.created_at).toLocaleDateString("tr-TR") : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => copyPath(f.storage_path)}
                        data-testid={`copy-url-btn-${f.id}`}
                        className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="URL'yi Kopyala"
                      >
                        <Copy size={13} />
                      </button>
                      <button
                        onClick={() => handleDelete(f.id, f.original_filename)}
                        data-testid={`delete-media-btn-${f.id}`}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Sil"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
